import type { PartialBlock } from '@blocknote/core';

import { presignDownload } from '@/lib/blocks/api';

import { FILE_BLOCK_ID_PROP, extractFileIdFromObjectUrl } from './file-block-persist';

export {
  FILE_BLOCK_ID_PROP,
  ensureFileIdsInBlocks,
  extractFileIdFromObjectUrl,
  stripFileUrlsForStorage,
} from './file-block-persist';

export async function resolveFileUrlsForEditor(blocks: PartialBlock[]): Promise<PartialBlock[]> {
  const urlByFileId = new Map<string, string>();

  async function urlForFileId(fileId: string): Promise<string> {
    const cached = urlByFileId.get(fileId);
    if (cached) {
      return cached;
    }
    const downloadUrl = await presignDownload(fileId);
    urlByFileId.set(fileId, downloadUrl);
    return downloadUrl;
  }

  async function visit(block: PartialBlock): Promise<PartialBlock> {
    const children = block.children?.length
      ? await Promise.all(block.children.map(visit))
      : block.children;

    if (!block.type || (block.type !== 'image' && block.type !== 'file')) {
      return children ? { ...block, children } : block;
    }

    const props = { ...((block.props as Record<string, unknown> | undefined) ?? {}) };
    let fileId: string | null = null;
    if (typeof props[FILE_BLOCK_ID_PROP] === 'string' && props[FILE_BLOCK_ID_PROP].length > 0) {
      fileId = props[FILE_BLOCK_ID_PROP];
    } else if (typeof props.url === 'string') {
      fileId = extractFileIdFromObjectUrl(props.url);
    }

    if (fileId) {
      props[FILE_BLOCK_ID_PROP] = fileId;
      const existingUrl = typeof props.url === 'string' ? props.url : '';
      // The server hydrates a usable (presigned https) URL on read; only presign
      // from the client when it is missing. Never let one failed/slow file block
      // the whole page — fall back to an empty url so the block renders as broken.
      if (!/^https?:\/\//i.test(existingUrl)) {
        try {
          props.url = await urlForFileId(fileId);
        } catch (err) {
          console.error('[CS-NET] presign-download failed for file', fileId, err);
          props.url = '';
        }
      }
    }

    return { ...block, props, children };
  }

  return Promise.all(blocks.map(visit));
}
